/**
 * UserAvatar + UserCell — carita de un usuario para identificarlo rápido en
 * los paneles de admin/superadmin (aseadores, clientes, propiedades, aseos).
 *
 * Regla de imagen (misma que el header/sidebar/perfil):
 *   foto subida (`avatarUrl`) > avatar generado (`avatarSeed` → Multiavatar)
 *   > Multiavatar del nombre > ícono neutro.
 *
 * NO usamos `<Avatar>` de AntD: cuando recibe `icon` Y `children`, prioriza el
 * `icon` y descarta el multiavatar (bug que dejaba el header con un ícono de
 * persona). Aquí renderizamos en un contenedor propio con el SVG dimensionado
 * por CSS (el SVG de Multiavatar trae solo viewBox, sin width/height).
 *
 * Solo los roles CON login llevan carita (cliente, aseador, admin, superadmin).
 * Para propietarios/trabajadores (sin login) el llamador pasa
 * `withAvatar={false}` a <UserCell> y se muestra solo el nombre.
 */

import React from "react";
import { UserOutlined } from "@ant-design/icons";
import multiavatar from "@multiavatar/multiavatar";
import "./UserAvatar.css";

export interface UserAvatarProps {
  nombre?: string | null;
  /** Foto subida (URL firmada del storage). Tiene prioridad. */
  avatarUrl?: string | null;
  /** Semilla Multiavatar (columna `avatar` del usuario). */
  avatarSeed?: string | null;
  /** Diámetro en px. Default 28. */
  size?: number;
  className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  nombre,
  avatarUrl,
  avatarSeed,
  size = 28,
  className,
}) => {
  const seed = avatarSeed || nombre || "";
  return (
    <span
      className={`user-avatar${className ? ` ${className}` : ""}`}
      style={{ width: size, height: size, minWidth: size }}
      aria-hidden
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={nombre || "avatar"} />
      ) : seed ? (
        <span
          className="user-avatar-svg"
          dangerouslySetInnerHTML={{ __html: multiavatar(seed) }}
        />
      ) : (
        <UserOutlined />
      )}
    </span>
  );
};

export interface UserCellProps extends UserAvatarProps {
  /** Si false, no muestra carita (roles sin login). Default true. */
  withAvatar?: boolean;
  /** Texto secundario opcional bajo el nombre (email, rut, etc.). */
  secondary?: React.ReactNode;
}

/**
 * Carita + nombre en línea, para columnas de tabla o tarjetas. Alinea vertical
 * y trunca el texto largo con ellipsis.
 */
export const UserCell: React.FC<UserCellProps> = ({
  nombre,
  avatarUrl,
  avatarSeed,
  size = 28,
  withAvatar = true,
  secondary,
  className,
}) => {
  return (
    <span className={`user-cell${className ? ` ${className}` : ""}`}>
      {withAvatar && (
        <UserAvatar
          nombre={nombre}
          avatarUrl={avatarUrl}
          avatarSeed={avatarSeed}
          size={size}
        />
      )}
      <span className="user-cell-text">
        <span className="user-cell-name">{nombre || "—"}</span>
        {secondary != null && secondary !== "" && (
          <span className="user-cell-secondary">{secondary}</span>
        )}
      </span>
    </span>
  );
};

export default UserAvatar;
